import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { getTickerChart, getUserTickerChart } from '../services/tickerApi';
import type { TimeWindow } from '../services/dashboardApi';

/**
 * Hook to fetch shared ticker chart data (market price series) from lazy endpoint
 */
export function useTickerChart(ticker: string | null, window: TimeWindow) {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['ticker-chart', ticker, window],
    queryFn: async () => {
      if (!ticker || !session?.access_token) {
        return null;
      }
      return getTickerChart({
        ticker,
        window,
        token: session.access_token,
      });
    },
    enabled: !!ticker && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
}

/**
 * Hook to fetch user-specific ticker chart data (cost basis, PnL) from lazy endpoint
 */
export function useUserTickerChart(
  accountId: string | null,
  ticker: string | null,
  window: TimeWindow
) {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['user-ticker-chart', accountId, ticker, window],
    queryFn: async () => {
      if (!accountId || !ticker || !session?.access_token) {
        return null;
      }
      return getUserTickerChart({
        accountId,
        ticker,
        window,
        token: session.access_token,
      });
    },
    enabled: !!accountId && !!ticker && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes (matches backend TTL)
  });
}
